import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "mobileMenu",
    "menuToggle",
    "modalContainer",
    "navigation",
    "desktopSidebar",
    "mainContent",
    "navLabel",
    "navIcon",
    "logoText",
    "profileDetails",
    "toggleIcon",
    "toggleButton",
    "sidebarContent",
    "navLink",
    "logoContainer",
    "profileButton",
    "profileSection",
    "profileWrapper",
    "notificationPanel",
    "notificationBell",
    "shareBookingLink"
  ]

  static values = {
    sidebarCollapsed: { type: Boolean, default: false },
    storageKey: { type: String, default: "operations-sidebar-collapsed" }
  }

  connect() {
    this.setupKeyboardShortcuts()
    this.loadSidebarPreference()
  }

  // Desktop Sidebar Toggle
  toggleDesktopSidebar() {
    this.sidebarCollapsedValue = !this.sidebarCollapsedValue
    this.saveSidebarPreference()
  }

  sidebarCollapsedValueChanged() {
    if (!this.hasDesktopSidebarTarget || !this.hasMainContentTarget) return

    const isCollapsed = this.sidebarCollapsedValue

    // Update sidebar width
    this.desktopSidebarTarget.classList.toggle('lg:w-72', !isCollapsed)
    this.desktopSidebarTarget.classList.toggle('lg:w-16', isCollapsed)

    // Update main content padding
    this.mainContentTarget.classList.toggle('lg:pl-72', !isCollapsed)
    this.mainContentTarget.classList.toggle('lg:pl-16', isCollapsed)

    // Update sidebar content padding (top section: logo + share link)
    if (this.hasSidebarContentTarget) {
      this.sidebarContentTarget.classList.toggle('p-6', !isCollapsed)
      this.sidebarContentTarget.classList.toggle('p-2', isCollapsed)
      this.sidebarContentTarget.classList.toggle('pt-14', isCollapsed)
      this.sidebarContentTarget.classList.toggle('pb-0', !isCollapsed)
    }

    // Update scrollable nav area padding
    const scrollableNav = this.desktopSidebarTarget.querySelector('.overflow-y-auto')
    if (scrollableNav) {
      scrollableNav.classList.toggle('px-6', !isCollapsed)
      scrollableNav.classList.toggle('px-2', isCollapsed)
    }

    // Toggle labels visibility
    this.navLabelTargets.forEach(label => {
      label.classList.toggle('lg:hidden', isCollapsed)
    })

    // Toggle nav icon margins and center icons when collapsed
    this.navIconTargets.forEach(icon => {
      icon.classList.toggle('mr-3', !isCollapsed)
    })

    // Toggle nav links to justify center when collapsed
    this.navLinkTargets.forEach(link => {
      link.classList.toggle('justify-center', isCollapsed)
      link.classList.toggle('px-3', !isCollapsed)
      link.classList.toggle('px-0', isCollapsed)
      // Remove left border active state when collapsed
      if (isCollapsed && link.classList.contains('border-l-4')) {
        link.classList.remove('border-l-4', 'border-black')
        link.dataset.hadBorder = 'true'
      } else if (!isCollapsed && link.dataset.hadBorder === 'true') {
        link.classList.add('border-l-4', 'border-black')
        delete link.dataset.hadBorder
      }
    })

    // Toggle logo container centering
    if (this.hasLogoContainerTarget) {
      this.logoContainerTarget.classList.toggle('justify-center', isCollapsed)
      this.logoContainerTarget.classList.toggle('space-x-3', !isCollapsed)
      this.logoContainerTarget.classList.toggle('mb-8', !isCollapsed)
      this.logoContainerTarget.classList.toggle('mb-4', isCollapsed)
    }

    // Toggle logo text
    if (this.hasLogoTextTarget) {
      this.logoTextTarget.classList.toggle('lg:hidden', isCollapsed)
    }

    // Toggle profile details
    this.profileDetailsTargets.forEach(detail => {
      detail.classList.toggle('lg:hidden', isCollapsed)
    })

    // Toggle profile section padding
    if (this.hasProfileSectionTarget) {
      this.profileSectionTarget.classList.toggle('p-4', !isCollapsed)
      this.profileSectionTarget.classList.toggle('p-2', isCollapsed)
    }

    // Toggle profile button centering
    if (this.hasProfileButtonTarget) {
      this.profileButtonTarget.classList.toggle('justify-center', isCollapsed)
      this.profileButtonTarget.classList.toggle('px-3', !isCollapsed)
      this.profileButtonTarget.classList.toggle('px-0', isCollapsed)
    }

    // Toggle profile wrapper flex-1 (remove when collapsed so avatar centers)
    if (this.hasProfileWrapperTarget) {
      this.profileWrapperTarget.classList.toggle('flex-1', !isCollapsed)
    }

    // Rotate toggle icon
    if (this.hasToggleIconTarget) {
      this.toggleIconTarget.classList.toggle('rotate-180', isCollapsed)
    }

    // Center toggle button when collapsed
    if (this.hasToggleButtonTarget) {
      this.toggleButtonTarget.classList.toggle('right-2', !isCollapsed)
      this.toggleButtonTarget.classList.toggle('left-1/2', isCollapsed)
      this.toggleButtonTarget.classList.toggle('-translate-x-1/2', isCollapsed)
    }

    // Update ARIA
    const toggleButton = this.element.querySelector('[aria-controls="desktop-sidebar"]')
    if (toggleButton) {
      toggleButton.setAttribute('aria-expanded', !isCollapsed)
    }
  }

  loadSidebarPreference() {
    try {
      const stored = localStorage.getItem(this.storageKeyValue)
      if (stored !== null) {
        this.sidebarCollapsedValue = stored === 'true'
      }
    } catch (e) {
      console.warn('localStorage unavailable:', e)
    }
  }

  saveSidebarPreference() {
    try {
      localStorage.setItem(this.storageKeyValue, this.sidebarCollapsedValue)
    } catch (e) {
      console.warn('Failed to save sidebar preference:', e)
    }
  }

  // Mobile Menu
  toggleMobileMenu() {
    this.mobileMenuTarget.classList.toggle("hidden")
    document.body.classList.toggle("overflow-hidden")
  }

  closeMobileMenu() {
    this.mobileMenuTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
  }

  openQuickBooking() {
    this.openModal("quick-booking")
  }

  openMenu() {
    this.toggleMobileMenu()
  }

  openModal(modalType) {
    // Load modal content via Turbo
    fetch(`/operations/modals/${modalType}`)
      .then(response => response.text())
      .then(html => {
        this.modalContainerTarget.innerHTML = html
        this.modalContainerTarget.classList.remove("hidden")
        document.body.classList.add("overflow-hidden")
      })
      .catch(error => {
        console.error('Failed to load modal:', error)
      })
  }

  closeModal() {
    this.modalContainerTarget.classList.add("hidden")
    this.modalContainerTarget.innerHTML = ""
    document.body.classList.remove("overflow-hidden")
  }


  // Notification Panel
  toggleNotifications() {
    if (this.hasNotificationPanelTarget) {
      this.notificationPanelTarget.classList.toggle("hidden")
    }
  }

  closeNotifications() {
    if (this.hasNotificationPanelTarget) {
      this.notificationPanelTarget.classList.add("hidden")
    }
  }

  setupKeyboardShortcuts() {
    document.addEventListener("keydown", (event) => {
      // ESC to close modals/menus/panels
      if (event.key === "Escape") {
        this.closeModal()
        this.closeMobileMenu()
        this.closeNotifications()
      }

      // Quick actions with Cmd/Ctrl key
      if (event.metaKey || event.ctrlKey) {
        switch (event.key) {
          case "k":
            event.preventDefault()
            this.openQuickBooking()
            break
          case "b":
            event.preventDefault()
            this.toggleDesktopSidebar()
            break
          case "1":
            event.preventDefault()
            window.location.href = "/operations/staffs"
            break
          case "2":
            event.preventDefault()
            window.location.href = "/operations/appointments"
            break
        }
      }
    })
  }

}
